<template>
  <!--
    IsometricView
    -------------------------------------------------------------------------
    Vista em PSEUDO-PERSPECTIVA isométrica (somente leitura) da mesma
    geometria do desenho em planta — como a metade de cima da prancha
    "DETALHE MARMORARIA" de referência: peça extrudada, recortes no tampo,
    cotas em vermelho e chamadas com líder pontilhado.

    Toda a matemática vive em engine/drawing/isometricProjection.js; aqui é
    só render declarativo (sem interação).
  -->
  <svg
    class="std-iso"
    :class="{ 'std-iso--dark': darkTheme }"
    :width="width"
    :height="height"
    :viewBox="`0 0 ${width} ${height}`"
  >
    <defs>
      <!-- textura de tijolo da PAREDE (mesma ideia da planta 2D): fiadas em
         amarração, só no topo do coto — as laterais usam um tom sólido
         mais escuro (sombra), a textura ficaria distorcida numa tira fina
         vista de lado. -->
      <pattern
        id="stdIsoBrickPattern"
        width="0.11"
        height="0.1"
        patternUnits="userSpaceOnUse"
      >
        <rect width="0.11" height="0.1" :fill="darkTheme ? '#8a4a34' : '#b5602f'" />
        <line x1="0" y1="0" x2="0.11" y2="0" :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="0.004" />
        <line x1="0" y1="0.05" x2="0.11" y2="0.05" :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="0.004" />
        <line x1="0" y1="0" x2="0" y2="0.05" :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="0.004" />
        <line x1="0.055" y1="0.05" x2="0.055" y2="0.1" :stroke="darkTheme ? '#2c150f' : '#f2e4d4'" stroke-width="0.004" />
      </pattern>
    </defs>
    <rect :width="width" :height="height" :fill="darkTheme ? '#0b141a' : '#ffffff'" />
    <g v-if="scene.bbox" :transform="sceneTransform">
      <!-- PAREDE (utility:wall): coto de referência baixo, atrás das peças —
           é o único elemento colorido da vista (tijolo), o resto segue o
           P&B das chapas. -->
      <polygon
        v-for="(poly, i) in wallSidePolygons"
        :key="`iso-wall-side-${i}`"
        :points="toPoints(poly.points)"
        :fill="darkTheme ? '#5a2c1c' : '#8a4a2e'"
        :stroke="darkTheme ? '#2c150f' : '#6b3a24'"
        :stroke-width="strokePx"
      />
      <polygon
        v-for="(poly, i) in wallTopPolygons"
        :key="`iso-wall-top-${i}`"
        :points="toPoints(poly.points)"
        fill="url(#stdIsoBrickPattern)"
        :stroke="darkTheme ? '#2c150f' : '#6b3a24'"
        :stroke-width="strokePx"
      />
      <!-- faces laterais (espessura da chapa) -->
      <polygon
        v-for="(poly, i) in sidePolygons"
        :key="`iso-side-${i}`"
        :points="toPoints(poly.points)"
        fill="#ececec"
        stroke="#173b33"
        :stroke-width="strokePx"
      />
      <!-- tampo -->
      <polygon
        v-for="(poly, i) in topPolygons"
        :key="`iso-top-${i}`"
        :points="toPoints(poly.points)"
        fill="#fdfdfd"
        stroke="#173b33"
        :stroke-width="strokePx"
      />
      <!-- recortes no tampo -->
      <polygon
        v-for="(poly, i) in cutoutPolygons"
        :key="`iso-cut-${i}`"
        :points="toPoints(poly.points)"
        fill="#ffffff"
        stroke="#173b33"
        :stroke-width="strokePx * 0.8"
      />
      <!-- líderes pontilhados (chamadas de recorte/borda) -->
      <line
        v-for="(l, i) in scene.lines"
        :key="`iso-line-${i}`"
        :x1="l.from.x"
        :y1="l.from.y"
        :x2="l.to.x"
        :y2="l.to.y"
        stroke="#666666"
        :stroke-width="strokePx * 0.8"
        :stroke-dasharray="`${strokePx * 3} ${strokePx * 2.2}`"
      />
      <circle
        v-for="(l, i) in scene.lines"
        :key="`iso-dot-${i}`"
        :cx="l.from.x"
        :cy="l.from.y"
        :r="strokePx * 2"
        fill="#666666"
      />
      <!-- cotas em vermelho (estilo da prancha de referência) -->
      <g v-if="showDimensions" v-for="(dim, i) in scene.dims" :key="`iso-dim-${i}`">
        <line
          :x1="dim.line.from.x"
          :y1="dim.line.from.y"
          :x2="dim.line.to.x"
          :y2="dim.line.to.y"
          stroke="#444444"
          :stroke-width="strokePx"
        />
        <text
          :x="dim.label.x"
          :y="dim.label.y"
          text-anchor="middle"
          fill="#444444"
          :font-size="fontPx"
          font-weight="700"
        >{{ dim.label.text }}</text>
      </g>
      <!-- rótulos / chamadas -->
      <text
        v-for="(lbl, i) in scene.labels"
        :key="`iso-lbl-${i}`"
        :x="lbl.x"
        :y="lbl.y"
        :text-anchor="lbl.kind === 'piece' ? 'middle' : 'start'"
        :fill="lbl.kind === 'piece' ? '#173b33' : '#4a544f'"
        :font-size="lbl.kind === 'piece' ? fontPx * 1.1 : fontPx"
        font-weight="700"
        style="letter-spacing: 0;"
      >{{ lbl.text }}</text>
    </g>
    <text
      v-else
      :x="width / 2"
      :y="height / 2"
      text-anchor="middle"
      fill="#9aa7a0"
      font-size="12"
    >Sem peças para a perspectiva</text>
  </svg>
</template>

<script>
import { normalizeFichaTecnica } from "./engine/drawing/normalizeFichaTecnica";
import { buildIsometricScene } from "./engine/drawing/isometricProjection";

export default {
  name: "IsometricView",
  props: {
    // aceita a ficha crua OU peças já normalizadas
    items: { type: Array, default: null },
    pieces: { type: Array, default: null },
    // elementos livres (drawingExtras.elements) — só a PAREDE (shape
    // 'bezier') é representada na perspectiva; o resto (linhas/textos)
    // continua exclusivo da planta 2D.
    elements: { type: Array, default: () => [] },
    width: { type: Number, default: 760 },
    height: { type: Number, default: 300 },
    // dimensões LÓGICAS do canvas da PLANTA (onde as positions % vivem):
    // usadas para reproduzir o layout da montagem na perspectiva
    planWidth: { type: Number, default: 760 },
    planHeight: { type: Number, default: 480 },
    showDimensions: { type: Boolean, default: false },
    darkTheme: { type: Boolean, default: false }
  },
  computed: {
    normalizedPieces() {
      if (Array.isArray(this.pieces)) return this.pieces;
      return normalizeFichaTecnica(this.items || []);
    },
    scene() {
      return buildIsometricScene(this.normalizedPieces, {
        planWidth: this.planWidth,
        planHeight: this.planHeight,
        walls: this.elements
      });
    },
    sidePolygons() {
      return this.scene.polygons.filter(p => p.kind === "side");
    },
    topPolygons() {
      return this.scene.polygons.filter(p => p.kind === "top");
    },
    wallSidePolygons() {
      return this.scene.polygons.filter(p => p.kind === "wall-side");
    },
    wallTopPolygons() {
      return this.scene.polygons.filter(p => p.kind === "wall-top");
    },
    cutoutPolygons() {
      return this.scene.polygons.filter(p => p.kind === "cutout");
    },
    // fit-to-content: escala a cena (em "metros projetados") para o SVG
    sceneScale() {
      const b = this.scene.bbox;
      if (!b) return 1;
      return Math.min(this.width / b.w, this.height / b.h);
    },
    sceneTransform() {
      const b = this.scene.bbox;
      if (!b) return "";
      const s = this.sceneScale;
      const tx = (this.width - b.w * s) / 2 - b.x * s;
      const ty = (this.height - b.h * s) / 2 - b.y * s;
      return `translate(${tx} ${ty}) scale(${s})`;
    },
    // traços/fontes definidos em px de TELA, convertidos p/ unidades da cena
    strokePx() {
      return 1.1 / this.sceneScale;
    },
    fontPx() {
      return 11 / this.sceneScale;
    }
  },
  methods: {
    toPoints(points) {
      return (points || []).map(p => `${p.x},${p.y}`).join(" ");
    }
  }
};
</script>

<style scoped>
.std-iso {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
